import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, BrainCircuit, Loader2, Radar, RefreshCw, Sparkles, TrendingUp } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { formatAddonPrice, getAiDashboardAddonPricing, isAiDashboardAddonEnabled } from '../lib/aiDashboardAddons';
import { formatCurrencyAmount } from '../lib/currency';
import { getErrorMessage } from '../lib/utils';
import { aiCommandAPI } from '../services/api';

const PANEL_CLASS = 'rounded-3xl border border-border bg-card text-foreground shadow-sm';
const SUB_PANEL_CLASS = 'rounded-2xl border border-border bg-muted';
const LABEL_CLASS = 'text-[11px] uppercase tracking-[0.18em] text-muted-foreground';
const TOOLTIP_STYLE = { backgroundColor: '#ffffff', border: '1px solid rgba(226,232,240,1)', borderRadius: '14px', color: '#0f172a' };

const FEATURE_CONFIG = {
  'attendance-insight': {
    addonKey: 'ai_attendance_insight',
    title: 'AI Attendance Insight',
    description: 'Dedicated attendance analytics, attendance-risk visibility, and workforce continuity signals.',
    accent: 'from-cyan-500/20 via-slate-950 to-slate-900',
    icon: Activity,
  },
  'hiring-forecast': {
    addonKey: 'ai_hiring_forecast',
    title: 'AI Hiring Forecast',
    description: 'Dedicated hiring demand forecasting, bottleneck visibility, and role-readiness signals.',
    accent: 'from-indigo-500/20 via-slate-950 to-slate-900',
    icon: Sparkles,
  },
  'payroll-forecast': {
    addonKey: 'ai_payroll_forecast',
    title: 'AI Payroll Forecast',
    description: 'Dedicated payroll forecasting, anomaly tracking, and compensation exposure signals.',
    accent: 'from-emerald-500/20 via-slate-950 to-slate-900',
    icon: TrendingUp,
  },
  'team-performance': {
    addonKey: 'ai_team_performance',
    title: 'AI Team Performance',
    description: 'Dedicated productivity, engagement, burnout, and skill-gap intelligence.',
    accent: 'from-sky-500/20 via-slate-950 to-slate-900',
    icon: BrainCircuit,
  },
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toPercent = (value) => `${Math.round(toNumber(value, 0))}%`;

const buildTenantFeatureContent = (featureKey, data) => {
  const overviewLookup = Object.fromEntries((data.workforce_overview || []).map((item) => [item.key, item]));
  const attendanceScore = toNumber(overviewLookup.attendance?.value);
  const productivityScore = toNumber(overviewLookup.productivity?.value);
  const hiringForecast = (data.forecasting?.hiring_demand || []).slice(-1)[0]?.value || 0;
  const productivityForecast = (data.forecasting?.productivity || []).slice(-1)[0]?.value || 0;
  const payrollForecast = (data.payroll_intelligence?.cost_forecasting || []).slice(-1)[0]?.value || 0;
  const hiringBottlenecks = data.talent_intelligence?.hiring_bottlenecks || [];
  const candidateScoring = data.talent_intelligence?.candidate_scoring || [];
  const teamMatrix = data.performance_intelligence?.team_performance_matrix || [];
  const burnoutPrediction = data.engagement_engine?.burnout_prediction || [];
  const overtimeAnomalies = data.payroll_intelligence?.overtime_anomalies || [];
  const compensationBenchmark = data.payroll_intelligence?.compensation_benchmark || [];
  const learningRecommendations = data.performance_intelligence?.learning_recommendations || [];
  const activityTimeline = (data.activity_timeline || []).slice(-6).map((row, index) => ({
    name: row.label || row.title || `Activity ${index + 1}`,
    value: toNumber(row.count ?? row.value, index + 1),
  }));

  if (featureKey === 'attendance-insight') {
    return {
      scopeLabel: 'Tenant AI Center',
      heroTitle: 'Attendance continuity and workforce reliability',
      heroDescription: 'Track attendance drift, activity pressure, and intervention recommendations from a focused AI workspace.',
      metrics: [
        { label: 'Attendance Score', value: `${attendanceScore}` },
        { label: 'Active Employees', value: `${overviewLookup.active?.value || 0}` },
        { label: 'AI Actions', value: `${(data.recommendations || []).length}` },
      ],
      chartTitle: 'Attendance Activity Trend',
      chartDescription: 'Recent AI activity signals related to attendance posture and workforce continuity.',
      chartData: activityTimeline,
      chartColor: '#22d3ee',
      primaryTitle: 'Attendance Actions',
      primaryDescription: 'AI recommendations prioritized for attendance stabilization.',
      primaryRows: (data.recommendations || []).slice(0, 4).map((row) => ({
        title: row.title,
        subtitle: row.summary || row.impact || 'Action recommended by the AI engine.',
      })),
      secondaryTitle: 'Attendance Signals',
      secondaryDescription: 'Attendance-oriented metrics that are isolated from the general AI dashboard.',
      secondaryRows: [
        { label: 'Attendance Delta', value: overviewLookup.attendance?.delta || 'Signal ready' },
        { label: 'Productivity', value: `${productivityScore}` },
        { label: 'Activity Events', value: `${(data.activity_timeline || []).length}` },
      ],
    };
  }

  if (featureKey === 'hiring-forecast') {
    return {
      scopeLabel: 'Tenant AI Center',
      heroTitle: 'Hiring demand and role-readiness forecasting',
      heroDescription: 'Review role demand, bottlenecks, and candidate quality signals inside a dedicated hiring intelligence view.',
      metrics: [
        { label: 'Forecast Demand', value: `${Math.round(toNumber(hiringForecast))}` },
        { label: 'Scored Roles', value: `${candidateScoring.length}` },
        { label: 'Bottlenecks', value: `${hiringBottlenecks.length}` },
      ],
      chartTitle: 'Hiring Funnel Pressure',
      chartDescription: 'Current funnel health used to project hiring demand and bottleneck pressure.',
      chartData: (data.talent_intelligence?.hiring_funnel || []).map((row) => ({
        name: row.stage,
        value: toNumber(row.value),
      })),
      chartColor: '#818cf8',
      primaryTitle: 'Top Hiring Signals',
      primaryDescription: 'Candidate scoring and bottleneck insights ready for recruiter follow-up.',
      primaryRows: [
        ...candidateScoring.slice(0, 2).map((row) => ({
          title: row.role,
          subtitle: row.recommendation || `Score ${row.score}`,
        })),
        ...hiringBottlenecks.slice(0, 2).map((row) => ({
          title: row.label,
          subtitle: row.value,
        })),
      ],
      secondaryTitle: 'Hiring Forecast Signals',
      secondaryDescription: 'Dedicated metrics that can be expanded into deeper recruiting analytics.',
      secondaryRows: [
        { label: 'Forecast Demand', value: `${Math.round(toNumber(hiringForecast))}` },
        { label: 'Candidate Score', value: `${candidateScoring[0]?.score || 0}` },
        { label: 'Pipeline Stages', value: `${(data.talent_intelligence?.hiring_funnel || []).length}` },
      ],
    };
  }

  if (featureKey === 'payroll-forecast') {
    return {
      scopeLabel: 'Tenant AI Center',
      heroTitle: 'Payroll forecasting and anomaly watch',
      heroDescription: 'Separate payroll forecasting from the broader AI dashboard and focus on financial movement, exposure, and compensation variance.',
      metrics: [
        { label: 'Payroll Volume', value: formatCurrencyAmount(data.payroll_intelligence?.payroll_volume || 0, 'INR') },
        { label: 'Forecast', value: formatCurrencyAmount(payrollForecast, 'INR') },
        { label: 'Anomalies', value: `${data.payroll_intelligence?.anomaly_count || 0}` },
      ],
      chartTitle: 'Payroll Forecast Trend',
      chartDescription: 'Predicted payroll movement isolated into its own workspace.',
      chartData: (data.payroll_intelligence?.cost_forecasting || []).map((row) => ({
        name: row.month || row.label,
        value: toNumber(row.value),
      })),
      chartColor: '#10b981',
      primaryTitle: 'Payroll Alerts',
      primaryDescription: 'AI-detected anomalies and compensation pressure highlights.',
      primaryRows: [
        ...overtimeAnomalies.slice(0, 2).map((row) => ({
          title: row.department,
          subtitle: `${row.hours} overtime hours flagged as ${row.risk} risk.`,
        })),
        ...compensationBenchmark.slice(0, 2).map((row) => ({
          title: row.department,
          subtitle: `${row.variance}% variance from benchmark`,
        })),
      ],
      secondaryTitle: 'Payroll Forecast Signals',
      secondaryDescription: 'Dedicated payroll intelligence metrics ready for future enhancement.',
      secondaryRows: [
        { label: 'Comp Departments', value: `${compensationBenchmark.length}` },
        { label: 'Overtime Flags', value: `${overtimeAnomalies.length}` },
        { label: 'Forecast Months', value: `${(data.payroll_intelligence?.cost_forecasting || []).length}` },
      ],
    };
  }

  return {
    scopeLabel: 'Tenant AI Center',
    heroTitle: 'Team productivity and performance signals',
    heroDescription: 'Focus on team performance, burnout pressure, and learning priorities from a dedicated AI Center module.',
    metrics: [
      { label: 'Productivity', value: `${productivityScore}` },
      { label: 'Forecast', value: `${Math.round(toNumber(productivityForecast))}` },
      { label: 'Skill Gaps', value: `${(data.performance_intelligence?.skill_gaps || []).length}` },
    ],
    chartTitle: 'Team Performance Matrix',
    chartDescription: 'Current performance ranking isolated from the main AI dashboard.',
    chartData: teamMatrix.slice(0, 6).map((row) => ({
      name: row.team,
      value: toNumber(row.performance),
    })),
    chartColor: '#38bdf8',
    primaryTitle: 'Performance Highlights',
    primaryDescription: 'Team performance and engagement patterns ready for action.',
    primaryRows: [
      ...teamMatrix.slice(0, 3).map((row) => ({
        title: row.team,
        subtitle: `Performance ${row.performance} with engagement ${row.engagement}.`,
      })),
      ...learningRecommendations.slice(0, 1).map((row) => ({
        title: 'Learning Recommendation',
        subtitle: row,
      })),
    ],
    secondaryTitle: 'Team Signals',
    secondaryDescription: 'Dedicated performance signals that can be expanded with deeper scorecards later.',
    secondaryRows: [
      { label: 'Burnout Risk', value: burnoutPrediction[0] ? toPercent(burnoutPrediction[0].value) : '0%' },
      { label: 'Skill Gaps', value: `${(data.performance_intelligence?.skill_gaps || []).length}` },
      { label: 'Teams Tracked', value: `${teamMatrix.length}` },
    ],
  };
};

const buildSuperadminFeatureContent = (featureKey, data) => {
  const tenantMatrix = data.tenant_health_matrix || [];
  const riskHeatmap = data.workforce_risk_heatmap || [];

  if (featureKey === 'attendance-insight') {
    const stabilityRows = [...tenantMatrix]
      .sort((left, right) => toNumber(right.workforce_stability) - toNumber(left.workforce_stability))
      .slice(0, 6);

    return {
      scopeLabel: 'Superadmin AI Center',
      heroTitle: 'Platform-wide attendance stability tracking',
      heroDescription: 'Monitor attendance hotspots and workforce stability across all tenants from a dedicated AI Center page.',
      metrics: [
        { label: 'Hotspots', value: `${riskHeatmap.length}` },
        { label: 'Tenants Tracked', value: `${tenantMatrix.length}` },
        { label: 'Forecast Points', value: `${(data.predictive_analytics?.workforce_forecasting || []).length}` },
      ],
      chartTitle: 'Attendance Continuity Forecast',
      chartDescription: 'Cross-tenant workforce continuity modeled by the AI layer.',
      chartData: (data.predictive_analytics?.workforce_forecasting || []).map((row) => ({
        name: row.month || row.label,
        value: toNumber(row.value),
      })),
      chartColor: '#22d3ee',
      primaryTitle: 'Attendance Hotspots',
      primaryDescription: 'Tenants with the sharpest attendance and workforce-risk pulse.',
      primaryRows: riskHeatmap.slice(0, 4).map((row) => ({
        title: row.tenant_name,
        subtitle: `Risk ${row.risk} with stability ${row.stability}.`,
      })),
      secondaryTitle: 'Workforce Leaders',
      secondaryDescription: 'High-stability tenants surfaced from the broader attendance model.',
      secondaryRows: stabilityRows.map((row) => ({
        label: row.tenant_name,
        value: `${row.workforce_stability}`,
      })),
    };
  }

  if (featureKey === 'hiring-forecast') {
    const hiringRows = [...tenantMatrix]
      .sort((left, right) => toNumber(right.hiring_efficiency) - toNumber(left.hiring_efficiency))
      .slice(0, 6);

    return {
      scopeLabel: 'Superadmin AI Center',
      heroTitle: 'Cross-tenant hiring forecast and hiring efficiency',
      heroDescription: 'Track hiring demand and tenant readiness from a dedicated platform-level module.',
      metrics: [
        { label: 'Forecast Points', value: `${(data.predictive_analytics?.hiring_demand_prediction || []).length}` },
        { label: 'Hiring Leaders', value: `${hiringRows.length}` },
        { label: 'Tracked Tenants', value: `${tenantMatrix.length}` },
      ],
      chartTitle: 'Hiring Demand Forecast',
      chartDescription: 'Modeled hiring demand across the tenant base.',
      chartData: (data.predictive_analytics?.hiring_demand_prediction || []).map((row) => ({
        name: row.month || row.label,
        value: toNumber(row.value),
      })),
      chartColor: '#818cf8',
      primaryTitle: 'Hiring Forecast Targets',
      primaryDescription: 'Tenants with the strongest hiring efficiency and commercial readiness.',
      primaryRows: hiringRows.slice(0, 4).map((row) => ({
        title: row.tenant_name,
        subtitle: `${row.region} with hiring efficiency ${row.hiring_efficiency}.`,
      })),
      secondaryTitle: 'Hiring Signals',
      secondaryDescription: 'Platform metrics that keep hiring analytics isolated from AI Dashboard.',
      secondaryRows: hiringRows.map((row) => ({
        label: row.tenant_name,
        value: `${row.hiring_efficiency}`,
      })),
    };
  }

  if (featureKey === 'payroll-forecast') {
    const payrollRows = [...tenantMatrix]
      .sort((left, right) => toNumber(right.payroll_volume) - toNumber(left.payroll_volume))
      .slice(0, 6);
    const payrollAlerts = data.ai_anomaly_tracker || [];

    return {
      scopeLabel: 'Superadmin AI Center',
      heroTitle: 'Payroll movement and anomaly forecasting',
      heroDescription: 'Use a dedicated payroll forecast page to watch platform-wide exposure and anomaly pressure.',
      metrics: [
        { label: 'Forecast Points', value: `${(data.predictive_analytics?.payroll_forecasting || []).length}` },
        { label: 'Payroll Alerts', value: `${payrollAlerts.length}` },
        { label: 'Tracked Tenants', value: `${payrollRows.length}` },
      ],
      chartTitle: 'Payroll Forecast',
      chartDescription: 'Predicted payroll movement across active subscriptions.',
      chartData: (data.predictive_analytics?.payroll_forecasting || []).map((row) => ({
        name: row.month || row.label,
        value: toNumber(row.value),
      })),
      chartColor: '#10b981',
      primaryTitle: 'Payroll Exposure Scan',
      primaryDescription: 'Financial anomalies and tenant-level exposure surfaced by AI.',
      primaryRows: payrollAlerts.slice(0, 4).map((row) => ({
        title: row.title,
        subtitle: row.tenant_name || row.description,
      })),
      secondaryTitle: 'Payroll Exposure',
      secondaryDescription: 'Top payroll-volume tenants in a dedicated payroll forecast module.',
      secondaryRows: payrollRows.map((row) => ({
        label: row.tenant_name,
        value: formatCurrencyAmount(row.payroll_volume || 0, 'INR'),
      })),
    };
  }

  const performanceRows = [...tenantMatrix]
    .sort((left, right) => toNumber(right.workforce_stability) - toNumber(left.workforce_stability))
    .slice(0, 6);

  return {
    scopeLabel: 'Superadmin AI Center',
    heroTitle: 'Team performance and workforce stability',
    heroDescription: 'Keep team performance insights isolated from AI Dashboard and ready for deeper future drilldowns.',
    metrics: [
      { label: 'Forecast Points', value: `${(data.predictive_analytics?.productivity_forecasting || []).length}` },
      { label: 'Performance Leaders', value: `${performanceRows.length}` },
      { label: 'Tracked Tenants', value: `${tenantMatrix.length}` },
    ],
    chartTitle: 'Productivity Forecast',
    chartDescription: 'Projected platform productivity and stability trend.',
    chartData: (data.predictive_analytics?.productivity_forecasting || []).map((row) => ({
      name: row.month || row.label,
      value: toNumber(row.value),
    })),
    chartColor: '#38bdf8',
    primaryTitle: 'Tenant Performance Signals',
    primaryDescription: 'Tenants with the strongest performance and stability posture.',
    primaryRows: performanceRows.slice(0, 4).map((row) => ({
      title: row.tenant_name,
      subtitle: `${row.region} with stability ${row.workforce_stability} and attrition ${row.attrition_risk}%.`,
    })),
    secondaryTitle: 'Performance Metrics',
    secondaryDescription: 'Platform-level performance measures kept in a dedicated AI Center module.',
    secondaryRows: performanceRows.map((row) => ({
      label: row.tenant_name,
      value: `${row.workforce_stability}`,
    })),
  };
};

const LockedAddonState = ({ addonKey, isSuperadmin }) => {
  const { user } = useAuth();
  const pricing = getAiDashboardAddonPricing(user?.limits || {}, addonKey);

  if (isSuperadmin) return null;

  return (
    <Card className="rounded-3xl border border-warning/30 bg-warning/10 shadow-sm">
      <CardHeader>
        <CardTitle className="text-foreground">Plan Add-on Required</CardTitle>
        <CardDescription className="text-muted-foreground">
          This AI Center module is isolated from AI Dashboard and unlocks through the selected subscription plan.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Additional monthly uplift: <span className="font-semibold text-foreground">{formatAddonPrice(pricing, 'monthly')}</span></p>
          <p className="text-sm text-muted-foreground">Additional yearly uplift: <span className="font-semibold text-foreground">{formatAddonPrice(pricing, 'yearly')}</span></p>
        </div>
        <div className="rounded-2xl border border-warning/30 bg-card px-4 py-3 text-sm text-muted-foreground">
          Current plan: <span className="font-medium text-foreground">{user?.subscriptionPlanName || user?.subscriptionPlanCode || 'Unassigned'}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const AICenterFeaturePage = ({ featureKey, scope = 'tenant' }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const config = FEATURE_CONFIG[featureKey];
  const isSuperadminScope = scope === 'superadmin';
  const Icon = config?.icon || BrainCircuit;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = isSuperadminScope
        ? await aiCommandAPI.getSuperadminCommandCenter()
        : await aiCommandAPI.getTenantCommandCenter();
      setData(response.data || {});
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to load ${config?.title || 'AI Center'} data`));
    } finally {
      setLoading(false);
    }
  }, [config?.title, isSuperadminScope]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isEnabled = isAiDashboardAddonEnabled(user?.limits || {}, config?.addonKey, isSuperadminScope || user?.isSuperadmin);
  const content = useMemo(
    () => (isSuperadminScope ? buildSuperadminFeatureContent(featureKey, data) : buildTenantFeatureContent(featureKey, data)),
    [data, featureKey, isSuperadminScope]
  );

  if (!config) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center rounded-3xl border border-border bg-card text-foreground shadow-sm">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading {config.title}...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="group relative overflow-hidden rounded-[28px] border border-border bg-gradient-to-br from-primary/10 via-card to-info/10 p-6 shadow-[0_22px_60px_rgba(15,23,42,0.08)]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-16 top-0 h-40 w-40 rounded-full bg-primary/10 blur-3xl transition duration-700 group-hover:scale-110" />
          <div className="absolute right-0 top-10 h-44 w-44 rounded-full bg-info/10 blur-3xl transition duration-700 group-hover:scale-110" />
        </div>
        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/80 px-3 py-1 text-xs font-medium text-primary shadow-sm backdrop-blur-sm">
              <Icon className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
              {content.scopeLabel}
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">{config.title}</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">{content.heroDescription}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {content.metrics.map((metric) => (
                <div key={metric.label} className="rounded-2xl border border-border bg-card/90 p-4 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{metric.label}</p>
                  <p className="mt-3 text-2xl font-semibold text-foreground">{metric.value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-4">
            <div className="rounded-2xl border border-border bg-card/92 p-5 shadow-sm backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:shadow-md">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">AI Center Focus</p>
                  <p className="mt-2 text-lg font-semibold text-foreground">{content.heroTitle}</p>
                </div>
                <Radar className="h-5 w-5 text-primary transition-transform duration-300 group-hover:scale-110" />
              </div>
              <p className="mt-4 text-sm leading-6 text-muted-foreground">
                {config.description}
              </p>
            </div>
            <div className="flex gap-3 xl:justify-end">
              <Button variant="outline" className="border-border bg-card/92 text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:bg-primary/10 hover:text-primary" onClick={loadData}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LockedAddonState addonKey={config.addonKey} isSuperadmin={isSuperadminScope || user?.isSuperadmin} />

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle className="text-foreground">{content.chartTitle}</CardTitle>
            <CardDescription className="text-muted-foreground">{content.chartDescription}</CardDescription>
          </CardHeader>
          <CardContent className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={content.chartData}>
                <CartesianGrid stroke="rgba(148,163,184,0.18)" vertical={false} />
                <XAxis dataKey="name" stroke="#64748b" tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" tickLine={false} axisLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]} fill={content.chartColor} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className={PANEL_CLASS}>
          <CardHeader>
            <CardTitle className="text-foreground">{content.secondaryTitle}</CardTitle>
            <CardDescription className="text-muted-foreground">{content.secondaryDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {content.secondaryRows.map((row) => (
              <div key={row.label} className={`${SUB_PANEL_CLASS} flex items-center justify-between gap-3 p-4`}>
                <div>
                  <p className="text-sm font-semibold text-foreground">{row.label}</p>
                </div>
                <div className="text-right">
                  <p className={LABEL_CLASS}>Value</p>
                  <p className="mt-1 text-lg font-semibold text-foreground">{row.value}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className={PANEL_CLASS}>
        <CardHeader>
          <CardTitle className="text-foreground">{content.primaryTitle}</CardTitle>
          <CardDescription className="text-muted-foreground">{content.primaryDescription}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 xl:grid-cols-2">
          {content.primaryRows.map((row, index) => (
            <div key={`${row.title}-${index}`} className={`${SUB_PANEL_CLASS} p-4`}>
              <p className="text-sm font-semibold text-foreground">{row.title}</p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{row.subtitle}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AICenterFeaturePage;
